/**
 * Notification Service (Web)
 * 
 * Chức năng: Service để gọi API liên quan đến notifications cho web app
 * Dùng để: 
 * - Lấy danh sách notifications
 * - Đánh dấu notification đã đọc
 * - Đánh dấu tất cả notifications đã đọc
 */

import { apiService } from './api.service';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  time: string;
  read: boolean;
  isToday?: boolean;
  avatar?: string;
  note?: string;
  attachmentName?: string;
  attachmentSize?: string;
  titleHighlights?: string[];
  createdAt?: string;
  data?: Record<string, any>;
  relatedResourceType?: string;
  relatedResourceId?: number;
  userId?: number; // ID của user liên quan (ví dụ: user tạo yêu cầu cập nhật)
}

interface NotificationListResponse {
  success: boolean;
  data: Notification[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class NotificationService {
  /**
   * Lấy danh sách notifications
   */
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<NotificationListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.unreadOnly) queryParams.append('unreadOnly', 'true');

      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.LIST}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await apiService.get<NotificationListResponse>(endpoint);
    } catch (error: any) {
      // Nếu API endpoint chưa có (404 hoặc Cannot GET), trả về empty response
      if (
        error?.message?.includes('Cannot GET') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Not Found')
      ) {
        if (import.meta.env.DEV) {
          console.warn('[NotificationService] API endpoint chưa có, trả về empty list');
        }
        return {
          success: true,
          data: [],
        };
      }
      // Re-throw các lỗi khác
      throw error;
    }
  }

  /**
   * Đánh dấu notification đã đọc
   */
  async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    try {
      return await apiService.post<{ success: boolean }>(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId));
    } catch (error: any) {
      // Nếu API endpoint chưa có, vẫn trả về success để không làm crash app
      if (
        error?.message?.includes('Cannot') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Not Found')
      ) {
        if (import.meta.env.DEV) {
          console.warn('[NotificationService] API endpoint chưa có, bỏ qua mark as read');
        }
        return { success: true };
      }
      throw error;
    }
  }

  /**
   * Đánh dấu tất cả notifications đã đọc
   */
  async markAllAsRead(): Promise<{ success: boolean }> {
    try {
      return await apiService.post<{ success: boolean }>(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);
    } catch (error: any) {
      // Nếu API endpoint chưa có, vẫn trả về success để không làm crash app
      if (
        error?.message?.includes('Cannot') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Not Found')
      ) {
        if (import.meta.env.DEV) {
          console.warn('[NotificationService] API endpoint chưa có, bỏ qua mark all as read');
        }
        return { success: true };
      }
      throw error;
    }
  }

  /**
   * Xóa 1 notification
   */
  async deleteNotification(notificationId: string): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiService.delete<{ success: boolean; message?: string }>(
        API_ENDPOINTS.NOTIFICATIONS.DELETE(notificationId)
      );
    } catch (error: any) {
      // Nếu API endpoint chưa có, vẫn trả về success để không làm crash app
      if (
        error?.message?.includes('Cannot') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Not Found')
      ) {
        if (import.meta.env.DEV) {
          console.warn('[NotificationService] API endpoint delete chưa có, bỏ qua delete');
        }
        return { success: true };
      }
      throw error;
    }
  }

  /**
   * Xóa nhiều notifications (bulk)
   */
  async deleteMany(notificationIds: string[]): Promise<{ success: boolean; message?: string; count?: number }> {
    try {
      return await apiService.post<{ success: boolean; message?: string; count?: number }>(
        API_ENDPOINTS.NOTIFICATIONS.DELETE_MANY,
        { ids: notificationIds }
      );
    } catch (error: any) {
      // Fallback: nếu backend chưa có bulk endpoint thì xóa lần lượt
      if (
        error?.message?.includes('Cannot') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Not Found')
      ) {
        const results = await Promise.allSettled(notificationIds.map((id) => this.deleteNotification(id)));
        const count = results.filter((r) => r.status === 'fulfilled').length;
        return { success: true, count };
      }
      throw error;
    }
  }
}

export const notificationService = new NotificationService();
